import Papa, { type ParseError } from "papaparse";
import { X, Upload, RefreshCcw } from "lucide-react";
import React, { useCallback, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAddDeck } from "@/hooks/useAddDeck";
import { useAllDecks } from "@/hooks/useAllDecks";
import { useReviewTime } from "@/hooks/useReviewTime";
import { getUniqueName } from "@/helpers/getUniqueName";

interface ParsedState {
  columns: string[]; // Column display labels
  rows: string[][]; // Each row is an array of string values
}

interface UIError {
  message: string;
  code?: string;
  row?: number;
}

interface ImportDeckDialogContentProps {
  closeDialog: () => void;
}

export const ImportDeckDialogContent = ({ closeDialog }: ImportDeckDialogContentProps) => {
  const [step, setStep] = useState<"upload" | "map">("upload");
  const [deckName, setDeckName] = useState<string>("Imported deck");
  const [firstRowHeader, setFirstRowHeader] = useState<boolean>(true);
  const [fileName, setFileName] = useState<string>("");
  const [parseErrors, setParseErrors] = useState<UIError[] | null>(null);
  const [parseNotice, setParseNotice] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedState | null>(null);
  const [uploadedText, setUploadedText] = useState<string | null>(null);
  const [questionColIndex, setQuestionColIndex] = useState<string | undefined>(undefined);
  const [answerColIndex, setAnswerColIndex] = useState<string | undefined>(undefined);
  const [isImporting, setIsImporting] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const addDeck = useAddDeck();
  const reviewTime = useReviewTime();
  const decks = useAllDecks();

  const safeToString = (v: unknown): string => {
    if (v === null || v === undefined) return "";
    if (typeof v === "object") {
      try {
        return JSON.stringify(v);
      } catch {
        return Object.prototype.toString.call(v);
      }
    }
    if (
      typeof v === "string" ||
      typeof v === "number" ||
      typeof v === "boolean" ||
      typeof v === "bigint"
    )
      return String(v);
    if (typeof v === "symbol") return v.toString();
    return "";
  };

  const reset = useCallback(() => {
    setParseErrors(null);
    setParseNotice(null);
    setParsed(null);
    setUploadedText(null);
    setQuestionColIndex(undefined);
    setAnswerColIndex(undefined);
    setDeckName("Imported deck");
    setFirstRowHeader(true);
    setStep("upload");
    if (inputRef.current) inputRef.current.value = "";
    setFileName("");
  }, []);

  // Helper to parse CSV text with provided options, wrapped in a Promise
  const parseText = useCallback(
    (text: string, options: Papa.ParseConfig) =>
      new Promise<Papa.ParseResult<unknown>>((resolve, reject) => {
        Papa.parse(text, {
          ...options,
          complete: (r) => resolve(r),
          error: (e: Error) => reject(e),
        });
      }),
    []
  );

  const onDropOrSelect = useCallback(
    async (file: File) => {
      setFileName(file.name);
      setParseErrors(null);
      setParseNotice(null);
      setParsed(null);
      const text = await file.text();
      setUploadedText(text);

      const baseOptions: Papa.ParseConfig = {
        header: firstRowHeader,
        skipEmptyLines: "greedy",
        transform: (v) => (typeof v === "string" ? v.trim() : v),
      };

      // First attempt: normal parsing
      let result: Papa.ParseResult<unknown>;
      try {
        result = await parseText(text, baseOptions);
      } catch (error: unknown) {
        setParseErrors([
          {
            message: error instanceof Error ? error.message : "File read error",
            code: "FileError",
            row: 0,
          },
        ]);
        return;
      }

      // Fallback attempt: if we saw InvalidQuotes, retry with quotes disabled
      const hadInvalidQuotes = result.errors.some((e: ParseError) => e.code === "InvalidQuotes");
      if (hadInvalidQuotes) {
        const fallbackOptions: Papa.ParseConfig = {
          ...baseOptions,
          delimiter: ",", // be explicit
          quoteChar: "\u0000", // treat normal quotes as data
          escapeChar: "\u0000",
        };
        const retry = await parseText(text, fallbackOptions);
        if (!retry.errors.length) {
          result = retry;
          setParseNotice(
            "We detected malformed quotes and parsed the file in a tolerant mode (quotes treated as literal characters)."
          );
        }
      }

      if (result.errors.length) {
        setParseErrors(
          result.errors.map((e: ParseError) => ({ message: e.message, code: e.code, row: e.row }))
        );
        return;
      }

      let columns: string[] = [];
      let rows: string[][] = [];
      if (firstRowHeader) {
        const data = result.data;
        const metaFields = result.meta.fields ?? [];
        columns = metaFields.length > 0 ? metaFields : Object.keys(data[0] ?? {});
        rows = (data as Record<string, unknown>[]).map((r) =>
          columns.map((c) => safeToString(r[c]))
        );
      } else {
        const data = result.data as unknown[][];
        const maxCols = data.reduce((m, r) => Math.max(m, r.length), 0);
        columns = Array.from({ length: maxCols }, (_, i) => `Column ${i + 1}`);
        rows = data.map((r) => columns.map((_, i) => safeToString(r[i])));
      }

      // Auto-map by simple heuristics
      const lc = columns.map((c) => c.toLowerCase());
      const findIdx = (alts: string[]) => lc.findIndex((c) => alts.some((a) => c.includes(a)));
      const qIdx = findIdx(["question", "front", "prompt", "q"]);
      const aIdx = findIdx(["answer", "back", "response", "a"]);
      if (qIdx >= 0) setQuestionColIndex(String(qIdx));
      if (aIdx >= 0) setAnswerColIndex(String(aIdx));

      setParsed({ columns, rows });
      setStep("map");
    },
    [firstRowHeader, parseText]
  );

  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (f) void onDropOrSelect(f);
    },
    [onDropOrSelect]
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const f = e.dataTransfer.files[0];
      if (f) void onDropOrSelect(f);
    },
    [onDropOrSelect]
  );

  const importDisabled = useMemo(() => {
    if (!parsed) return true;
    const qc = questionColIndex !== undefined && questionColIndex !== "";
    const ac = answerColIndex !== undefined && answerColIndex !== "";
    return !qc || !ac || !deckName.trim();
  }, [parsed, questionColIndex, answerColIndex, deckName]);

  const handleImport = useCallback(async () => {
    if (!parsed || importDisabled) return;
    setIsImporting(true);
    try {
      const q = Number(questionColIndex);
      const a = Number(answerColIndex);
      const cards = parsed.rows
        .map((row) => ({
          question: safeToString(row[q]).trim(),
          answer: safeToString(row[a]).trim(),
        }))
        .filter((r) => r.question && r.answer)
        .map((r) => ({ ...r, nextReview: reviewTime }));

      if (cards.length === 0) {
        setParseErrors([
          {
            code: "NoRows",
            message: "No valid rows found. Ensure both mapped columns contain data and try again.",
            row: 0,
          },
        ]);
        setIsImporting(false);
        return;
      }

      const finalName = decks
        ? getUniqueName(
            deckName.trim() || "Imported deck",
            decks.map((d) => d.name)
          )
        : deckName.trim() || "Imported deck";
      await addDeck({
        name: finalName,
        cards: cards as { question: string; answer: string; nextReview: number }[],
      });
      // Ensure dialog state is clean for the next open
      reset();
      closeDialog();
    } catch (e: unknown) {
      const message =
        e && typeof e === "object" && "message" in e
          ? String((e as { message: unknown }).message)
          : "Import failed";
      setParseErrors([
        {
          code: "ImportFailed",
          message,
          row: 0,
        },
      ]);
    } finally {
      setIsImporting(false);
    }
  }, [
    parsed,
    importDisabled,
    questionColIndex,
    answerColIndex,
    reviewTime,
    addDeck,
    deckName,
    closeDialog,
    decks,
    reset,
  ]);

  const handleUseHeaderCheckboxClick: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    async (e) => {
      const newVal = e.target.checked;
      setFirstRowHeader(newVal);
      if (!uploadedText) return;
      // Re-parse with new header setting, with tolerant fallback
      setParseErrors(null);
      setParseNotice(null);
      const baseOptions: Papa.ParseConfig = {
        header: newVal,
        skipEmptyLines: "greedy",
        transform: (v) => (typeof v === "string" ? v.trim() : v),
      };
      let result: Papa.ParseResult<unknown>;
      try {
        result = await parseText(uploadedText, baseOptions);
      } catch (error: unknown) {
        setParseErrors([
          {
            message: error instanceof Error ? error.message : "File read error",
            code: "FileError",
            row: 0,
          },
        ]);
        return;
      }
      const hadInvalidQuotes = result.errors.some((er: ParseError) => er.code === "InvalidQuotes");
      if (hadInvalidQuotes) {
        const fallbackOptions: Papa.ParseConfig = {
          ...baseOptions,
          delimiter: ",",
          quoteChar: "\u0000",
          escapeChar: "\u0000",
        };
        const retry = await parseText(uploadedText, fallbackOptions);
        if (!retry.errors.length) {
          result = retry;
          setParseNotice(
            "We detected malformed quotes and parsed the file in a tolerant mode (quotes treated as literal characters)."
          );
        }
      }
      if (result.errors.length) {
        setParseErrors(
          result.errors.map((er: ParseError) => ({
            message: er.message,
            code: er.code,
            row: er.row,
          }))
        );
        return;
      }
      // Update parsed and auto-map again
      if (newVal) {
        const data = result.data as Record<string, unknown>[];
        const metaFields = result.meta.fields ?? [];
        const columns = metaFields.length > 0 ? metaFields : Object.keys(data[0] ?? {});
        const rows = data.map((r) => columns.map((c) => safeToString(r[c])));
        setParsed({ columns, rows });
        const lc = columns.map((c) => c.toLowerCase());
        const findIdx = (alts: string[]) => lc.findIndex((c) => alts.some((a) => c.includes(a)));
        const qIdx = findIdx(["question", "front", "prompt", "q"]);
        const aIdx = findIdx(["answer", "back", "response", "a"]);
        if (qIdx >= 0) setQuestionColIndex(String(qIdx));
        if (aIdx >= 0) setAnswerColIndex(String(aIdx));
      } else {
        const data = result.data as unknown[][];
        const maxCols = data.reduce((m, r) => Math.max(m, r.length), 0);
        const columns = Array.from({ length: maxCols }, (_, i) => `Column ${i + 1}`);
        const rows = data.map((r) => columns.map((_, i) => safeToString(r[i])));
        setParsed({ columns, rows });
        const lc = columns.map((c) => c.toLowerCase());
        const findIdx = (alts: string[]) => lc.findIndex((c) => alts.some((a) => c.includes(a)));
        const qIdx = findIdx(["question", "front", "prompt", "q"]);
        const aIdx = findIdx(["answer", "back", "response", "a"]);
        if (qIdx >= 0) setQuestionColIndex(String(qIdx));
        if (aIdx >= 0) setAnswerColIndex(String(aIdx));
      }
    },
    [parseText, uploadedText]
  );

  return (
    <DialogContent className="sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle className="text-xl">Import Deck from CSV</DialogTitle>
        <DialogDescription hidden={true}>
          Upload a CSV then map columns to Question and Answer.
        </DialogDescription>
        <DialogClose className="absolute right-4 top-4 opacity-70 hover:opacity-100">
          <X className="h-4 w-4" />
        </DialogClose>
      </DialogHeader>

      {step === "upload" && (
        <div className="space-y-4">
          <div
            className="border-2 border-dashed rounded-md p-8 text-center hover:bg-muted/30 cursor-pointer"
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => inputRef.current?.click()}
            role="button"
          >
            <Upload className="w-10 h-10 mx-auto mb-3 text-gray-500" />
            <p className="font-medium">Drop a CSV file here or click to browse</p>
            <p className="text-sm text-gray-500 mt-1">
              The file should have one column for questions and one column for answers.
            </p>
            {fileName && <p className="text-sm mt-2">Selected: {fileName}</p>}
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={onFileChange}
            />
          </div>

          <div className="flex items-center justify-end">
            <Button variant="ghost" size="sm" onClick={reset}>
              <RefreshCcw className="w-4 h-4 mr-2" /> Reset
            </Button>
          </div>

          {parseErrors && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm">
              <p className="font-semibold text-red-700 mb-1">Couldn't parse the file</p>
              <ul className="list-disc ml-5 text-red-700 space-y-1">
                {parseErrors.map((err) => (
                  <li key={`err-${String(err.code)}-${String(err.row)}-${err.message}`}>
                    Row {err.row}: {err.message} ({err.code})
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-gray-700">
                Please check the file format and try again. CSV should be UTF-8 encoded.
              </p>
            </div>
          )}
          {!parseErrors && parseNotice && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
              <p className="text-amber-800">{parseNotice}</p>
            </div>
          )}
        </div>
      )}

      {step === "map" && parsed && (
        <div className="space-y-5">
          {!parseErrors && parseNotice && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
              <p className="text-amber-800">{parseNotice}</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Deck name</label>
              <Input value={deckName} onChange={(e) => setDeckName(e.target.value)} />
            </div>
            <div className="flex items-center justify-start md:justify-end gap-2">
              <input
                id="header-checkbox"
                type="checkbox"
                checked={firstRowHeader}
                onChange={handleUseHeaderCheckboxClick}
              />
              <label htmlFor="header-checkbox" className="text-sm">
                First row is a header
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">Question column</label>
              <Select value={questionColIndex} onValueChange={setQuestionColIndex}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select column" />
                </SelectTrigger>
                <SelectContent>
                  {parsed.columns.map((c, i) => {
                    const key = `qcol-${c}-${i}-${parsed.columns.length}`;
                    return (
                      <SelectItem key={key} value={String(i)}>
                        {c || `Column ${i + 1}`}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Answer column</label>
              <Select value={answerColIndex} onValueChange={setAnswerColIndex}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select column" />
                </SelectTrigger>
                <SelectContent>
                  {parsed.columns.map((c, i) => {
                    const key = `acol-${c}-${i}-${parsed.columns.length}`;
                    return (
                      <SelectItem key={key} value={String(i)}>
                        {c || `Column ${i + 1}`}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-2">Preview (first 3 rows)</p>
            <div className="border rounded-md overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {parsed.columns.map((c, i) => {
                      const key = `thead-${c}-${i}-${parsed.columns.length}`;
                      return (
                        <th key={key} className="text-left px-3 py-2 font-medium">
                          {c || `Column ${i + 1}`}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {parsed.rows.slice(0, 3).map((r) => {
                    const rowKey = `row-${JSON.stringify(r).slice(0, 40)}`;
                    return (
                      <tr key={rowKey} className="border-t">
                        {r.map((cell, ci) => {
                          const cellKey = `${rowKey}-${ci}-${safeToString(cell).slice(0, 10)}`;
                          return (
                            <td
                              key={cellKey}
                              className={
                                "px-3 py-1 " +
                                (String(ci) === questionColIndex || String(ci) === answerColIndex
                                  ? "bg-secondary/40"
                                  : "")
                              }
                            >
                              {cell}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {parseErrors && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm">
              <p className="font-semibold text-red-700 mb-1">There were issues</p>
              <ul className="list-disc ml-5 text-red-700 space-y-1">
                {parseErrors.map((err) => {
                  const key = `err-${String(err.code)}-${String(err.row)}-${err.message}`;
                  return (
                    <li key={key}>
                      Row {err.row}: {err.message} ({err.code})
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={reset}>
              Back
            </Button>
            <Button onClick={handleImport} disabled={importDisabled ? true : isImporting}>
              {isImporting ? "Importing…" : "Import"}
            </Button>
          </div>
        </div>
      )}
    </DialogContent>
  );
};
